// Two sites, one build: the journal (nummius.com) and Nummius Academy (academy.nummius.com). nginx serves the same
// bundle on both hosts; the hostname picks the routes (App.js). The dev site server says which site a port is and where
// the other one lives (window.__NM_SITE / window.__NM_SITES, scripts/dev/server.cjs).

const host = () => (typeof window === 'undefined' ? '' : window.location.hostname || '');

/** True on the Academy's own site. */
export function isAcademySite() {
  if (typeof window === 'undefined') return false;
  if (window.__NM_SITE) return window.__NM_SITE === 'academy';
  return host().startsWith('academy.');
}

/** { main, academy } origins without a trailing slash; academy is null where no Academy site exists (plain localhost). */
export function siteOrigins() {
  if (typeof window === 'undefined') return { main: '', academy: null };
  if (window.__NM_SITES) return window.__NM_SITES;
  const { protocol, port, origin } = window.location;
  const hostname = host();
  const p = port ? `:${port}` : '';
  if (hostname.startsWith('academy.')) return { main: `${protocol}//${hostname.slice(8)}${p}`, academy: origin };
  if (/^(localhost|[\d.]+|\[[\d:a-f]+\])$/i.test(hostname)) return { main: origin, academy: null };
  return { main: origin, academy: `${protocol}//academy.${hostname.replace(/^www\./, '')}${p}` };
}

/** Absolute Academy URL for an Academy path ('/classes/12'). */
export function academyUrl(path = '/') {
  const { academy } = siteOrigins();
  return `${academy || ''}${path}`;
}

/** Absolute journal URL for a journal path ('/app/trades'). */
export function mainUrl(path = '/') {
  return `${siteOrigins().main}${path}`;
}

/** Where a login lands when nothing else was asked for. */
export function homePath() {
  return isAcademySite() ? '/' : '/app';
}

/** The Academy path for an old journal-side Academy path: '/app/edu/classes/3?x' -> '/classes/3?x'. */
export function academyPathFromLegacy(pathname, search = '', hash = '') {
  const rest = pathname.replace(/^\/app\/edu(?=\/|$)/, '') || '/';
  return `${rest}${search}${hash}`;
}

/** A same-origin `?next=` path (like Login.js's getSafeNextPath), or null. */
export function safeNextPath() {
  const next = new URLSearchParams(window.location.search).get('next');
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.includes('\\')) return null;
  try {
    if (new URL(next, window.location.origin).origin !== window.location.origin) return null;
  } catch (_) {
    return null;
  }
  return next;
}

// Where a visitor was headed when they had to log in. `?next=` carries it through /login; this copy (one per site,
// 1 hour) also survives the detours a login can take: sign up, confirm email, forgot password.
const NEXT_KEY = 'nm.loginNext';
const NEXT_TTL = 3600 * 1000;
const AUTH_PATH = /^\/(login|register|verify-email|forgot-password|reset-password)([/?#]|$)/;

/** Remember `path` as the place to land after logging in (ignored for auth pages and '/'). */
export function rememberNext(path) {
  if (!path || !path.startsWith('/') || path.startsWith('//') || AUTH_PATH.test(path) || path === '/') return;
  try { localStorage.setItem(NEXT_KEY, JSON.stringify({ path, at: Date.now() })); } catch (_) { /* storage off */ }
}

/** The remembered destination, or null (expired or none). Clears it. */
export function takeRememberedNext() {
  try {
    const raw = localStorage.getItem(NEXT_KEY);
    localStorage.removeItem(NEXT_KEY);
    const v = raw && JSON.parse(raw);
    return v && typeof v.path === 'string' && Date.now() - v.at < NEXT_TTL && !AUTH_PATH.test(v.path) ? v.path : null;
  } catch (_) {
    return null;
  }
}

/** True when `token` (a JWT) is a demo session's (demo accounts: backend/demo_accounts.py). Read only, never trusted for access. */
export function isDemoToken(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return false;
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))).demo === true;
  } catch (_) {
    return false;
  }
}

/** Where an ended session goes: a finished demo goes to sign up, anything else back to the login with this page as next. */
export function expiredSessionPath(token) {
  return isDemoToken(token) ? '/register?demo=ended' : loginPath();
}

/** '/login?next=<this page>' (remembering it too), or plain '/login' from an auth page. */
export function loginPath(path) {
  const here = path || (typeof window === 'undefined' ? '' : window.location.pathname + window.location.search + window.location.hash);
  if (!here || AUTH_PATH.test(here)) return '/login';
  rememberNext(here);
  return here === '/' ? '/login' : `/login?next=${encodeURIComponent(here)}`;
}

/** Where to go once logged in: ?next=, else the remembered destination, else this site's home. */
export function afterLoginPath() {
  const next = safeNextPath();
  const saved = takeRememberedNext();
  return next || saved || homePath();
}

/** Keep ?next= on links between the auth pages (log in <-> sign up <-> forgot password). */
export function withNext(path) {
  const next = typeof window === 'undefined' ? null : safeNextPath();
  return next ? `${path}${path.includes('?') ? '&' : '?'}next=${encodeURIComponent(next)}` : path;
}
